// komponent wise-select - ujednolicony selekt z możliwością wyboru wariantu i trybu

import React, { useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactSelect, { Options, MultiValue, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ChevronDown, X } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { ErrorMessage } from 'components/controls';
import { IOption, WiseSelectProps } from 'components/wise/WiseSelect/types';
import { getPortalStyles } from 'components/wise/WiseSelect/getPortalStyles';

import sassStyles from 'theme/components/wise/WiseSelect/WiseSelect.module.scss';
import defaultStyles from 'theme/components/controls/Select/styles/defaultStyles/defaultStyles.module.scss';
import borderedStyles from 'theme/components/controls/Select/styles/borderedStyles/borderedStyles.module.scss';
import borderlessStyles from 'theme/components/controls/Select/styles/borderlessStyles/borderlessStyles.module.scss';
import smallStyles from 'theme/components/controls/Select/styles/smallStyles/smallStyles.module.scss';

// Komponent wskaźnika dropdown - zdefiniowany poza komponentem dla lepszej wydajności
const DropdownIndicator = () => <ChevronDown />;


const WiseSelect = <T extends Record<string, any>>({
  displayVariant = 'dropdown',
  mode = 'singlechoice',
  label,
  options = [],
  value,
  onChange,
  placeholder,
  clearable = false,
  disabled = false,
  error,
  selectedOption,
  separatorMultiChoice = '|',
  variant = 'default',
  styleGroup,
  controlId,
  onOpenChange,
  onCreateOption,
  optionFontSize
}: WiseSelectProps<T>) => {
  const { t } = useTranslation();

  // Memoizacja stylów wariantu
  const styles = useMemo(() => {
    switch (variant) {
      case 'bordered':
        return borderedStyles;
      case 'small':
        return smallStyles;
      case 'borderless':
        return borderlessStyles;
      default:
        return defaultStyles;
    }
  }, [variant]);

  // Typ komponentu: CreatableSelect - z opcją tworzenia nowej opcji, ReactSelect - domyślny selekt
  const SelectComponent = useMemo(
    () => (onCreateOption ? CreatableSelect : ReactSelect),
    [onCreateOption]
  );

  // Zaznaczona opcja (jak w obecnym Select)
  const currentOption = useMemo(() => {
    if (mode === 'multichoice') {
      if (Array.isArray(value)) {
        return options.filter((option) => 
          value.some((val) => val.toString() === option.value.toString())
        );
      }
      if (typeof value === 'string' && value.length > 0) {
        return value
          .split(separatorMultiChoice)
          .map((val) => options.find((opt) => opt.value.toString() === val))
          .filter(Boolean) as Options<IOption<T>>;
      }
      return [];
    } else {
      // singlechoice
      return selectedOption || options.find((option) => option.value === value) || null;
    }
  }, [value, options, selectedOption, mode, separatorMultiChoice]);

  // Handler zmiany wartości - dla dropdown variant
  const handleSelectChange = useCallback(
    (option: any) => {
      if (mode === 'singlechoice') {
        const singleOption = option as SingleValue<IOption<T>>;
        // TypeScript nie może zawęzić typu z union, więc używamy type assertion
        (onChange as (item: T | null) => void)(singleOption?.item || null);
      } else {
        // multichoice - przekazujemy całą tablicę Options
        const multiOptions = option as MultiValue<IOption<T>>;
        (onChange as (items: Options<IOption<T>>) => void)(multiOptions);
      }
    },
    [onChange, mode]
  );

  // Handler usuwania chipa w modalu variant (multichoice)
  const handleChipRemove = useCallback(
    (indexToRemove: number) => {
      if (mode === 'multichoice' && Array.isArray(currentOption)) {
        const newOptions = currentOption.filter((_, index) => index !== indexToRemove);
        (onChange as (items: Options<IOption<T>>) => void)(newOptions);
      }
    },
    [currentOption, mode, onChange]
  );

  // Handler czyszczenia wartości w modal variant (singlechoice)
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (mode === 'singlechoice') {
        (onChange as (item: T | null) => void)(null);
      }
    },
    [mode, onChange]
  );

  // Tekst wyświetlany w przycisku (dla modala)
  const displayValue = useMemo(() => {
    if (mode === 'singlechoice') {
      const selected = currentOption as SingleValue<IOption<T>>;
      return selected ? selected.label : placeholder || t('Wybierz...');
    } else {
      // multichoice
      const selected = currentOption as Options<IOption<T>>;
      if (selected.length === 0) {
        return placeholder || t('Wybierz...');
      }
      return selected; // Zwracamy tablicę opcji, żeby móc je renderować jako chipsy
    }
  }, [currentOption, mode, placeholder, t]);

  // Klasy CSS z hierarchią stylów
  const wrapperClasses = classnames(
    sassStyles.componentWrapper,
    sassStyles.wiseSelect,
    'StylePath-Components-Wise-WiseSelect'
  );

  // Placeholder tekst - memoizowany
  const placeholderText = useMemo(() => placeholder || t('Wybierz...'), [placeholder, t]);

  // Warunki dla lepszej czytelności
  const isMultichoice = mode === 'multichoice';
  const isEmptySelection = isMultichoice && Array.isArray(currentOption) && currentOption.length === 0;

  // Memoizacja classNames dla react-select
  const reactSelectClassNames = useMemo(
    () => ({
      container: () => styles.container,
      control: ({ menuIsOpen, isDisabled, isFocused }: any): string =>
        classnames(
          styles.control,
          sassStyles.controlHover,
          { [styles.controlOpen]: menuIsOpen },
          { [sassStyles.controlDisabled]: isDisabled },
          { [sassStyles.controlFocused]: isFocused }
        ),
      option: ({ isSelected }: any): string =>
        classnames(styles.option, { [styles.optionSelected]: isSelected }),
      indicatorSeparator: (): string => styles.indicatorSeparator,
      indicatorsContainer: (): string => styles.indicatorsContainer,
      dropdownIndicator: (): string => styles.dropdownIndicator,
      valueContainer: (): string => styles.valueContainer,
      singleValue: (): string => classnames(styles.singleValue, sassStyles.singleValueFont),
      placeholder: (): string => classnames(styles.placeholder, sassStyles.placeholder, sassStyles.placeholderFont),
      menu: (): string => styles.menu,
      menuList: (): string => styles.menuList,
      multiValue: (): string => sassStyles.multiValue,
      multiValueLabel: (): string => sassStyles.multiValueLabel,
      multiValueRemove: (): string => sassStyles.multiValueRemove
    }),
    [styles]
  );

  // Format label dla CreatableSelect - memoizowany
  const formatCreateLabel = useCallback(
    (label: string) => `${t('Stwórz')} "${label}"`,
    [t]
  );

  // Dropdown variant z react-select
  if (displayVariant === 'dropdown') {
    return (
      <div
        className={wrapperClasses}
        data-style-group={styleGroup}
        data-control-id={controlId}
        data-wise-component="select">
        {label && <label className={sassStyles.label}>{label}</label>}
        <SelectComponent
          options={options}
          placeholder={placeholderText}
          classNames={reactSelectClassNames}
          onChange={handleSelectChange}
          value={currentOption}
          components={{ DropdownIndicator }}
          isSearchable={!!onCreateOption}
          isDisabled={disabled}
          onCreateOption={onCreateOption}
          formatCreateLabel={formatCreateLabel}
          isClearable={clearable}
          isMulti={isMultichoice}
          menuPortalTarget={styleGroup === 'portal' ? document.body : undefined}
          menuPosition={styleGroup === 'portal' ? 'fixed' : 'absolute'}
          styles={styleGroup === 'portal' ? getPortalStyles({ fontSize: optionFontSize }) : undefined}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    );
  }

  // Modal variant - tylko przycisk, modal renderowany przez rodzica
  return (
    <div
      className={wrapperClasses}
      data-style-group={styleGroup}
      data-control-id={controlId}
      data-wise-component="select">
      {label && <label className={sassStyles.label}>{label}</label>}
      <button
        type="button"
        className={classnames(sassStyles.triggerButton, {
          [sassStyles.disabled]: disabled,
          [sassStyles.error]: !!error
        })}
        onClick={() => !disabled && onOpenChange?.(true)}
        disabled={disabled}>
        <span className={sassStyles.triggerButtonContent}>
          {isMultichoice && Array.isArray(displayValue) ? (
            isEmptySelection ? (
              <span className={classnames(sassStyles.triggerButtonText, sassStyles.placeholderText)}>
                {placeholderText}
              </span>
            ) : (
              <div className={sassStyles.selectedChips}>
                {displayValue.map((option, index) => (
                  <span key={`${option.value}-${index}`} className={sassStyles.selectedChip}>
                    <span className={sassStyles.selectedChipLabel}>{option.label}</span>
                    {clearable && (
                      <button
                        type="button"
                        className={sassStyles.selectedChipRemove}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChipRemove(index);
                        }}
                        aria-label={t('Usuń')}>
                        <X size={20} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )
          ) : (
            <span className={sassStyles.triggerButtonTextWrapper}>
              <span 
                className={classnames(sassStyles.triggerButtonText, {
                  [sassStyles.placeholderText]: !currentOption || isEmptySelection
                })}>
                {typeof displayValue === 'string' 
                  ? displayValue
                  : (currentOption && !Array.isArray(currentOption))
                    ? (currentOption as IOption<T>).label
                    : placeholderText}
              </span>
              {clearable && currentOption && !isEmptySelection && (
                <button
                  type="button"
                  className={sassStyles.triggerButtonClear}
                  onClick={handleClear}
                  aria-label={t('Wyczyść')}>
                  <X size={20} />
                </button>
              )}
            </span>
          )}
        </span>
        <ChevronDown className={sassStyles.triggerButtonIcon} />
      </button>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

// Memoizacja komponentu dla lepszej wydajności
export default memo(WiseSelect) as typeof WiseSelect;
